
import { useState, useEffect } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'

interface CreateGroupModalProps {
  isOpen: boolean
  onClose: () => void
  onCreateGroup: (name: string, userIds: string[]) => Promise<void>
}

interface UserResult {
  id: string
  username: string
  avatar_url: string | null
}

const MAX_GROUP_MEMBERS = 100

const CreateGroupModal = ({ isOpen, onClose, onCreateGroup }: CreateGroupModalProps) => {
  const { user } = useAuth()
  const [step, setStep] = useState<'details' | 'members'>('details')
  const [groupName, setGroupName] = useState('')
  const [groupIcon, setGroupIcon] = useState('ri-group-line')
  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState<UserResult[]>([])
  const [selectedUsers, setSelectedUsers] = useState<UserResult[]>([])
  const [searching, setSearching] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  const iconOptions = [
    'ri-group-line',
    'ri-team-line',
    'ri-movie-2-line',
    'ri-film-line',
    'ri-star-line',
    'ri-heart-line',
    'ri-chat-smile-3-line',
    'ri-gamepad-line',
    'ri-music-2-line',
    'ri-book-open-line',
    'ri-cup-line',
    'ri-fire-line'
  ]

  useEffect(() => {
    if (!isOpen) {
      setStep('details')
      setGroupName('')
      setGroupIcon('ri-group-line')
      setSearchQuery('')
      setResults([])
      setSelectedUsers([])
      setError('')
    }
  }, [isOpen])

  const handleSearch = async (query: string) => {
    setSearchQuery(query)
    if (query.trim().length < 2) {
      setResults([])
      return
    }

    setSearching(true)
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, username, avatar_url')
        .neq('id', user?.id || '')
        .ilike('username', `%${query.trim()}%`)
        .limit(20)

      if (!error && data) {
        // Filter out already selected users
        const filteredResults = data.filter(
          u => !selectedUsers.some(s => s.id === u.id)
        )
        setResults(filteredResults)
      }
    } catch (err) {
      console.error('Search error:', err)
    } finally {
      setSearching(false)
    }
  }

  const handleSelectUser = (userToAdd: UserResult) => {
    if (selectedUsers.length >= MAX_GROUP_MEMBERS - 1) {
      setError(`Maximum ${MAX_GROUP_MEMBERS} members allowed (including you)`)
      return
    }
    setSelectedUsers(prev => [...prev, userToAdd])
    setResults(prev => prev.filter(u => u.id !== userToAdd.id))
    setSearchQuery('')
  }

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId))
    setError('')
  }

  const handleNext = () => {
    if (!groupName.trim()) {
      setError('Please enter a group name')
      return
    }
    setError('')
    setStep('members')
  }

  const handleBack = () => {
    setStep('details')
    setError('')
  }

  const handleCreate = async () => {
    if (selectedUsers.length < 1) {
      setError('Please add at least 1 member')
      return
    }

    setCreating(true)
    setError('')
    try {
      await onCreateGroup(groupName.trim(), selectedUsers.map(u => u.id))
      onClose()
    } catch (err) {
      console.error('Error creating group:', err)
      setError('Failed to create group. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {step === 'members' && (
              <button
                onClick={handleBack}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-lg text-gray-500"></i>
              </button>
            )}
            <h3 className="text-base font-bold text-gray-900">
              {step === 'details' ? 'Create Group Chat' : 'Add Members'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg text-gray-500"></i>
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-5 pt-4">
          <div className="flex items-center gap-2">
            <div className={`flex-1 h-1 rounded-full ${step === 'details' ? 'bg-purple-500' : 'bg-purple-500'}`}></div>
            <div className={`flex-1 h-1 rounded-full ${step === 'members' ? 'bg-purple-500' : 'bg-gray-200'}`}></div>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Step {step === 'details' ? '1' : '2'} of 2 • {step === 'details' ? 'Group details' : `Add up to ${MAX_GROUP_MEMBERS} members`}
          </p>
        </div>

        {step === 'details' ? (
          /* Step 1: Group Details */
          <div className="p-5">
            {/* Group Icon */}
            <div className="flex justify-center mb-5">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white">
                <i className={`${groupIcon} text-3xl`}></i>
              </div>
            </div>

            {/* Icon Selection */}
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-500 mb-2">Choose Icon</label>
              <div className="flex flex-wrap gap-2 justify-center">
                {iconOptions.map((icon) => (
                  <button
                    key={icon}
                    onClick={() => setGroupIcon(icon)}
                    className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all cursor-pointer ${
                      groupIcon === icon
                        ? 'bg-purple-100 text-purple-600 ring-2 ring-purple-500'
                        : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <i className={`${icon} text-xl`}></i>
                  </button>
                ))}
              </div>
            </div>

            {/* Group Name */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 mb-2">Group Name</label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Enter group name..."
                maxLength={50}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{groupName.length}/50</p>
            </div>

            {error && (
              <p className="text-xs text-red-500 mb-4 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>
                {error}
              </p>
            )}

            <button
              onClick={handleNext}
              className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-medium rounded-xl hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
            >
              Next: Add Members
            </button>
          </div>
        ) : (
          /* Step 2: Add Members */
          <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 280px)' }}>
            {/* Selected Members */}
            {selectedUsers.length > 0 && (
              <div className="px-5 pt-4 pb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500">
                    Selected ({selectedUsers.length}/{MAX_GROUP_MEMBERS - 1})
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                  {selectedUsers.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center gap-1.5 pl-1 pr-2 py-1 bg-purple-50 rounded-full"
                    >
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-[10px] font-semibold overflow-hidden">
                        {u.avatar_url ? (
                          <img src={u.avatar_url} alt="" className="w-full h-full object-cover object-top" />
                        ) : (
                          u.username?.[0]?.toUpperCase() || 'U'
                        )}
                      </div>
                      <span className="text-xs font-medium text-purple-700 max-w-[80px] truncate">
                        {u.username}
                      </span>
                      <button
                        onClick={() => handleRemoveUser(u.id)}
                        className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-purple-100 cursor-pointer"
                      >
                        <i className="ri-close-line text-xs text-purple-500"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search */}
            <div className="px-5 py-3">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search users to add..."
                  className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>

            {/* Search Results */}
            <div className="flex-1 overflow-y-auto px-2 pb-2 min-h-[150px]">
              {searching ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
                </div>
              ) : results.length > 0 ? (
                results.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => handleSelectUser(u)}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-purple-50 transition-colors cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-semibold text-sm overflow-hidden flex-shrink-0">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="" className="w-full h-full object-cover object-top" />
                      ) : (
                        u.username?.[0]?.toUpperCase() || 'U'
                      )}
                    </div>
                    <span className="text-sm font-medium text-gray-800">{u.username}</span>
                    <i className="ri-add-circle-line text-purple-500 ml-auto text-lg"></i>
                  </button>
                ))
              ) : searchQuery.trim().length >= 2 ? (
                <div className="text-center py-8">
                  <i className="ri-user-search-line text-3xl text-gray-300 block mb-2"></i>
                  <p className="text-sm text-gray-400">No users found</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="ri-user-add-line text-3xl text-gray-300 block mb-2"></i>
                  <p className="text-sm text-gray-400">Search for users to add to the group</p>
                </div>
              )}
            </div>

            {/* Error & Create Button */}
            <div className="px-5 py-4 border-t border-gray-100">
              {error && (
                <p className="text-xs text-red-500 mb-3 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {error}
                </p>
              )}
              <button
                onClick={handleCreate}
                disabled={selectedUsers.length < 1 || creating}
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-medium rounded-xl hover:shadow-lg transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="ri-group-line"></i>
                    Create Group ({selectedUsers.length + 1} members)
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CreateGroupModal
